import React from "react";
import { C1_TYPES, C2_TYPES, RISK_CFG } from "../../../data/riskAssessmentData";
import type { C2HistoryEntry } from "../../../types/risk-assessment.types";
import type { PageView } from "./useRiskAssessmentState";

interface HistoryViewProps {
  history: C2HistoryEntry[];
  reset: () => void;
  setPageView: (v: PageView) => void;
  deleteHistoryEntry: (id: string) => void;
}

const HistoryView: React.FC<HistoryViewProps> = ({
  history,
  reset,
  setPageView,
  deleteHistoryEntry,
}) => (
  <main
    style={{
      flex: 1,
      padding: 24,
      maxWidth: 900,
      margin: "0 auto",
      width: "100%",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 6,
          height: 28,
          borderRadius: 3,
          background: "linear-gradient(180deg, #4f46e5, #7c3aed)",
          flexShrink: 0,
        }} />
        <h2
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: "-0.4px",
            background: "linear-gradient(135deg, #1B3A6B 0%, #4f46e5 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Completed Assessments
        </h2>
      </div>
    </div>

    {history.length === 0 ? (
      <div style={{ textAlign: "center", padding: 60, color: "#94A3B8" }}>
        <div style={{ fontSize: 48 }}>📂</div>
        <p>
          No completed assessments yet. Complete a C2 risk assessment to see it
          here.
        </p>
      </div>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {history.map((h) => {
          const hRc = RISK_CFG[h.riskRating];
          const hC1 = C1_TYPES.find((t) => t.id === h.c1Type);
          const hC2 = C2_TYPES.find((t) => t.id === h.c2EntityType);
          return (
            <div
              key={h.id}
              style={{
                background: "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)",
                borderRadius: 14,
                padding: 18,
                display: "flex",
                alignItems: "center",
                gap: 16,
                boxShadow: `0 4px 16px rgba(0,0,0,.08), 0 0 0 1px ${hRc.border}`,
                border: `1px solid ${hRc.border}`,
                transition: "all 0.25s ease",
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 10,
                  background: hRc.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  flexShrink: 0,
                }}
              >
                {hC1?.icon || "🛡️"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: "#1E293B",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {h.c2Name}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#64748B",
                    marginTop: 2,
                  }}
                >
                  {/*
                    AL-420 §2: the entity-type badge now shows the stored,
                    registry-derived value or says it is not recorded. The name
                    heuristic that used to fill the gap is gone.

                    It was the cause of the badge disagreement on this very list:
                    it rendered "Association" here while the mapper collapsed the
                    same input to `body_corporate`, so one row could describe
                    itself two different ways depending on which code path you
                    read.
                  */}
                  {hC1?.label} → {hC2?.icon || "🏢"}{" "}
                  <span
                    style={hC2 ? undefined : { color: "#94A3B8", fontStyle: "italic" }}
                    data-testid="history-entity-type"
                  >
                    {hC2?.label || "Type not recorded"}
                  </span>{" "}
                  • {h.flaggedCount}/
                  {h.totalQuestions} flagged •{" "}
                  {new Date(h.completedAt).toLocaleDateString("en-AU", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#475569",
                    marginTop: 5,
                    fontWeight: 600,
                  }}
                  title={
                    !h.createdByName && h.createdBy
                      ? `Signer identifier: ${h.createdBy}`
                      : undefined
                  }
                >
                  Signed Off By: {h.createdByName || "Unknown"}
                </div>
              </div>
              <span
                style={{
                  background: hRc.bg,
                  color: hRc.color,
                  border: `1px solid ${hRc.border}`,
                  borderRadius: 20,
                  padding: "3px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {hRc.label}
              </span>
            </div>
          );
        })}
      </div>
    )}
  </main>
);

export default HistoryView;
